import { getCookieDomain, getRedirectUrl } from "@/utils/auth";
import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

// Track if we're already handling session expiration to prevent loops
let isHandlingExpiration = false;

/**
 * Custom fetch wrapper that intercepts 401 responses and triggers session expiration
 */
const customFetch = async (url, options) => {
  const response = await fetch(url, options);

  // Check for 401 Unauthorized responses (except for auth endpoints)
  if (response.status === 401 && !url.includes('/auth/')) {
    if (!isHandlingExpiration && !window.location.pathname.startsWith('/auth')) {
      isHandlingExpiration = true;
      console.warn('[Supabase] 401 Unauthorized detected, redirecting to login');

      // Clear session data
      localStorage.removeItem('app-session');

      // Small delay to prevent race conditions, then redirect
      setTimeout(() => {
        window.location.href = '/auth/login?reason=session_expired';
      }, 100);
    }
  }

  return response;
};
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl) {
  console.error("Missing VITE_SUPABASE_URL environment variable");
  throw new Error("Supabase URL is required but not configured");
}

if (!supabaseAnonKey) {
  console.error("Missing VITE_SUPABASE_ANON_KEY environment variable");
  throw new Error("Supabase Anon Key is required but not configured");
}

// Determine if we're in development or production
const isDevelopment = import.meta.env.DEV;
const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

// Configure Supabase client with enhanced PKCE flow handling and cookie support
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storageKey: "app-session",
    // Use cookies for better cross-browser and incognito support
    storageType: "cookies",
    // Provide localStorage as fallback
    storage: typeof window !== "undefined" ? window.localStorage : null,
    // CRITICAL: Disable automatic session detection to prevent duplicate token exchanges
    // We handle this manually in AuthCallback.jsx
    detectSessionInUrl: false,
    flowType: "pkce",
    // Set a longer expiration for flow state to improve reliability
    pkceTimeout: 30 * 60, // 30 minutes for OAuth flow
    cookieOptions: {
      secure: window.location.protocol === "https:",
      sameSite: "Lax", // Better browser compatibility
      domain: getCookieDomain(),
      path: "/",
      maxAge: 7200, // 2 hours
    },
    // Use the redirect URL function for consistency
    site_url: getRedirectUrl(),
    // Only log auth state changes in development
    onAuthStateChange: (event, session) => {
      if (event === "USER_UPDATED" && !session) {
        console.warn("User updated but no session available");
      }
    },
  },
  global: {
    // Use custom fetch to intercept 401 responses
    fetch: customFetch,
    headers: {
      "X-Client-Info": "supabase-js",
      "X-Client-Secret": import.meta.env.VITE_SUPABASE_ANON_KEY,
      "x-application-name": "artifact-chat",
    },
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Specialized sign-in with OAuth with enhanced error handling and logging
export async function signInWithOAuth(provider, options = {}) {
  // Always ensure we use the correct redirectTo URL based on environment
  const redirectTo = getRedirectUrl();

  // Add enhanced scopes and params based on provider
  const enhancedOptions = {
    ...options,
    redirectTo,
    scopes: options.scopes || "email profile",
    queryParams: {
      ...options.queryParams,
      // Force consent screen for fresh token
      prompt: "consent",
      access_type: "offline",
    },
  };

  // For specific providers, add additional options
  if (provider === "google") {
    enhancedOptions.queryParams.include_granted_scopes = "true";
  }

  return await supabase.auth.signInWithOAuth({
    provider,
    options: enhancedOptions,
  });
}

// Magic Link Authentication Functions
export async function signInWithMagicLink(email, options = {}) {
  try {
    // Get correct redirect URL based on environment
    const redirectUrl = getRedirectUrl();

    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectUrl,
        shouldCreateUser: true, // Allow automatic user creation
        ...options,
      },
    });

    if (error) {
      console.error("Magic link sign-in error:", error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error("Magic link sign-in exception:", error);
    return { data: null, error };
  }
}

export async function signUpWithMagicLink(
  email,
  displayName = null,
  options = {}
) {
  try {
    // Get correct redirect URL based on environment
    const redirectUrl = getRedirectUrl();

    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectUrl,
        shouldCreateUser: true, // Ensure user creation is allowed
        data: {
          full_name: displayName || "",
        },
        ...options,
      },
    });

    if (error) {
      console.error("Magic link signup error:", error);

      // Check if error is related to email sending
      if (
        error.message?.includes("send email") ||
        error.message?.includes("gomail") ||
        error.message?.includes("550") ||
        error.message?.includes("domain")
      ) {
        console.warn("Email sending issue detected:", error.message);
        return {
          data,
          error: {
            ...error,
            message:
              "Unable to send verification email. Please use social login instead.",
            code: "email_verification_issue",
          },
        };
      }

      return { data: null, error };
    }

    // For magic link, we don't create profile here since the user isn't confirmed yet
    // Profile creation will happen in the auth callback after email verification
    return { data, error: null };
  } catch (error) {
    console.error("Magic link signup exception:", error);
    return { data: null, error };
  }
}

export async function verifyOtp(email, token, type = "email") {
  try {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type,
    });

    if (error) {
      console.error("OTP verification error:", error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error("OTP verification exception:", error);
    return { data: null, error };
  }
}

// Update signUpWithEmail to use magic link instead of password
export async function signUpWithEmail(email, password, displayName = null) {
  // Redirect to magic link signup instead
  return await signUpWithMagicLink(email, displayName);
}

export async function signInWithEmail(email, password) {
  // Redirect to magic link sign-in instead
  return await signInWithMagicLink(email);
}

export async function signOut() {
  return await supabase.auth.signOut();
}

export async function getCurrentUser() {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (user) {
    try {
      // Get user profile data including API key
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Error fetching user profile:", error);
        return user;
      }
    } catch (error) {
      console.error("Error in getCurrentUser:", error);
    }
  }
  return user;
}

export async function getSession() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session;
}

export async function updateUserApiKey(userId, apiKey) {
  return await supabase.from("profiles").upsert(
    {
      id: userId,
      openai_api_key: apiKey,
      updated_at: new Date().toISOString(),
    },
    {
      onConflict: "id",
    }
  );
}

// Enhanced version that uses better error handling and reporting
export async function exchangeCodeForSession(code) {
  if (!code) {
    return {
      data: null,
      error: new Error("No code provided for authentication"),
    };
  }

  try {
    // Track exchange attempts to prevent rate limiting
    const recentExchangeAttempts = parseInt(
      localStorage.getItem("oauth_exchange_attempts") || "0"
    );
    const lastExchangeTime = parseInt(
      localStorage.getItem("oauth_last_exchange_time") || "0"
    );
    const now = Date.now();

    // If recent attempts, add delay to prevent rate limiting
    if (recentExchangeAttempts > 2 && now - lastExchangeTime < 60000) {
      console.warn("Multiple recent exchange attempts, adding delay");
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    // Track this attempt
    localStorage.setItem(
      "oauth_exchange_attempts",
      String(recentExchangeAttempts + 1)
    );
    localStorage.setItem("oauth_last_exchange_time", String(now));

    // Clear tracking after 1 minute
    setTimeout(() => {
      localStorage.setItem("oauth_exchange_attempts", "0");
    }, 60000);

    const result = await supabase.auth.exchangeCodeForSession(code);

    // On success, reset attempts
    if (!result.error) {
      localStorage.setItem("oauth_exchange_attempts", "0");
    }

    return result;
  } catch (error) {
    console.error("Exception during code exchange:", error);

    // Detect rate limiting
    if (
      error.message?.includes("429") ||
      error.message?.includes("rate limit")
    ) {
      localStorage.setItem("oauth_exchange_attempts", "0");
      error.isRateLimit = true;
    }

    throw error;
  }
}

export default supabase;
